import React from 'react'
import PropTypes from 'prop-types'
import { Stack } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../../../../../../generic/smartSelect'
import { PERIOD_KEYS, PERIOD_OPTIONS } from '../../utils/periodFilter'

const PERIOD_KEY_LIST = PERIOD_OPTIONS.map((option) => option.key)
const PERIOD_LABEL_BY_KEY = Object.fromEntries(PERIOD_OPTIONS.map((option) => [option.key, option.label]))

/**
 * Compact period select shared by the widget's main list and the modal's tabs: narrows the
 * planned-jobs list by a time window (Today/This week/This month/This year) on top of the
 * `startDate` column, degrading to "All" (no filter) by default.
 *
 * UI polish: reuses the repo's own SmartSelect -- the same component as the modal's "Component"
 * select, so height/variant/border match it automatically -- with a stable PERIOD_KEYS value and
 * a translated display label via `optionLabelTransform` (the underlying value is never affected,
 * only the visible text). A calendar icon next to the label makes it read clearly as "this is
 * about the jobs' time window".
 *
 * @param {Object} props
 * @param {string} props.value - One of PERIOD_KEYS
 * @param {Function} props.onChange - (nextPeriodKey) => void
 */
const PeriodSelect = ({ value, onChange }) => {
  const { t_ } = useSmartTranslation()

  return (
    <Stack direction='row' spacing={0.5} alignItems='center'>
      <CalendarMonthIcon color='action' fontSize='small' />
      <SmartSelect
        width='12rem'
        label={t_('Jobs period')}
        value={value}
        selectableOptions={PERIOD_KEY_LIST}
        optionLabelTransform={(key) => t_(PERIOD_LABEL_BY_KEY[key])}
        sortValues={false}
        autoSelectFirst={false}
        onChange={onChange}
      />
    </Stack>
  )
}

PeriodSelect.propTypes = {
  value: PropTypes.oneOf(Object.values(PERIOD_KEYS)).isRequired,
  onChange: PropTypes.func.isRequired
}

export default PeriodSelect
